'use client'

import Link from 'next/link'
import Image from 'next/image'
import { useMetrica } from 'next-yandex-metrica'
import { Headphones, BookOpen, Mail, Calendar, Music, Play, ExternalLink } from 'lucide-react'
import clsx from 'clsx'

import { ReactComponent as TelegramIcon } from '@/assets/img/telegram-logo.svg'

// Constants for consistent styling
const SOCIAL_ICON_DIMENSIONS = 'w-10 h-10 min-w-10 max-w-10 max-h-10 min-h-10'

// Social media links with real URLs
const socialLinks = [
  {
    id: 'youtube',
    name: 'YouTube',
    url: 'https://www.youtube.com/@nuvertal?sub_confirmation=1',
    icon: (
      <Image
        src="/assets/img/youtube.svg"
        alt="YouTube"
        width={40}
        height={40}
        className={SOCIAL_ICON_DIMENSIONS}
      />
    ),
    bgColor: '',
    description: 'Записи эфиров, подкасты и прямые трансляции',
  },
  {
    id: 'telegram',
    name: 'Telegram канал',
    url: 'https://t.me/nuvertal_music',
    icon: (
      <Image
        src="/assets/img/telegram-logo-circled.svg"
        alt="Telegram"
        width={40}
        height={40}
        className={SOCIAL_ICON_DIMENSIONS}
      />
    ),
    bgColor: '',
    description: 'Оперативные обновления и анонсы',
  },
  {
    id: 'twitch',
    name: 'Twitch',
    url: 'https://www.twitch.tv/nuvertal_dnb',
    icon: (
      <Image
        src="/assets/img/twitch.svg"
        alt="Twitch"
        width={40}
        height={40}
        className={SOCIAL_ICON_DIMENSIONS}
      />
    ),
    bgColor: '',
    description: 'Прямые трансляции и общение в чате',
  },
  {
    id: 'vk',
    name: 'VK',
    url: 'https://vk.com/nuvertal_music',
    icon: (
      <Image
        src="/assets/img/vk.svg"
        alt="VK"
        width={40}
        height={40}
        className={SOCIAL_ICON_DIMENSIONS}
      />
    ),
    bgColor: '',
    description: 'Новости и анонсы выпусков',
  },
  {
    id: 'rutube',
    name: 'RuTube',
    url: 'https://rutube.ru/channel/35723907/',
    icon: (
      <Image
        src="/assets/img/rutube.svg"
        alt="RuTube"
        width={40}
        height={40}
        className={SOCIAL_ICON_DIMENSIONS}
      />
    ),
    bgColor: '',
    description: 'Прямые трансляции и общение в чате',
  },
  {
    id: 'boosty',
    name: 'Boosty',
    url: 'https://boosty.to/nuvertal',
    icon: (
      <Image
        src="/assets/img/boosty.png"
        alt="Boosty"
        width={40}
        height={40}
        className={SOCIAL_ICON_DIMENSIONS}
      />
    ),
    bgColor: '',
    description: 'Эксклюзивный контент и поддержка',
  },
]

// Additional music platform links
const musicPlatformLinks = [
  {
    id: 'spotify',
    name: 'Spotify',
    url: 'https://open.spotify.com/artist/1a9tzNkt3whGzmgKs7EvpD',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        className="w-5 h-5 text-radio-blue"
      >
        <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm4.059 14.406c-.192.2-.508.204-.704.012-1.94-1.185-4.383-1.453-7.255-.796-.267.062-.536-.105-.598-.37-.061-.267.105-.535.371-.598 3.154-.722 5.851-.412 8.01.916.196.192.2.508.012.704l.164.132zm1.082-2.406c-.243.25-.64.258-.886.015-2.223-1.37-5.61-1.767-8.24-.967-.337.102-.69-.088-.792-.425-.102-.337.087-.69.424-.792 3.006-.915 6.74-.47 9.307 1.094.246.243.254.64.015.886l.172.189zm.094-2.503c-.266.27-.7.28-.965.013-2.55-1.564-6.79-1.71-9.238-.94-.392.124-.8-.094-.924-.485-.123-.392.094-.8.485-.923 2.82-.896 7.514-.731 10.434 1.108.265.266.275.7.013.965l.195.262zM7 17.5c0 .828-.672 1.5-1.5 1.5S4 18.328 4 17.5 4.672 16 5.5 16 7 16.672 7 17.5zm10 0c0 .828-.672 1.5-1.5 1.5s-1.5-.672-1.5-1.5.672-1.5 1.5-1.5 1.5.672 1.5 1.5z" />
      </svg>
    ),
    description: 'Слушайте треки на Spotify',
  },
  {
    id: 'vk-music',
    name: 'VK Музыка',
    url: 'https://vk.com/artist/nuvertal',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        className="w-5 h-5 text-radio-blue"
      >
        <path d="M21.579 6.855c.14-.465 0-.806-.662-.806h-2.193c-.558 0-.813.295-.953.619 0 0-1.115 2.719-2.695 4.482-.51.513-.743.675-1.021.675-.139 0-.341-.162-.341-.627V6.855c0-.558-.161-.806-.626-.806H9.642c-.348 0-.558.258-.558.504 0 .528.79.65.87 2.138v3.228c0 .707-.127.836-.407.836-.743 0-2.551-2.729-3.624-5.853-.209-.607-.42-.852-.98-.852H2.752c-.627 0-.752.295-.752.619 0 .582.743 3.462 3.461 7.271 1.812 2.601 4.363 4.011 6.687 4.011 1.393 0 1.565-.313 1.565-.853v-1.966c0-.626.133-.752.574-.752.324 0 .882.164 2.183 1.417 1.486 1.486 1.732 2.153 2.567 2.153h2.192c.626 0 .939-.313.759-.932-.197-.615-.907-1.51-1.849-2.569-.512-.604-1.277-1.254-1.51-1.579-.325-.419-.231-.604 0-.976.001 0 2.672-3.761 2.95-5.04z" />
      </svg>
    ),
    description: 'Слушайте треки на VK Музыке',
  },
  {
    id: 'yandex-music',
    name: 'Яндекс Музыка',
    url: 'https://music.yandex.ru/artist/4811984',
    icon: <Music className="w-5 h-5 text-radio-blue" />,
    description: 'Слушайте треки на Яндекс Музыке',
  },
  {
    id: 'liquor',
    name: 'Подкаст Liquor',
    url: 'https://www.youtube.com/watch?v=moB5wVoXKyk',
    icon: <Play className="w-5 h-5 text-radio-blue" />,
    description: 'Drum&Bass подкаст на YouTube',
  },
]

export default function ContactsSection() {
  const { reachGoal } = useMetrica()
  return (
    <section
      id="contacts"
      className="py-20 pt-12 bg-gradient-to-b from-black/40 to-black/20 relative"
    >
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle at 25px 25px, rgba(255, 255, 255, 0.2) 2%, transparent 0%)',
            backgroundSize: '50px 50px',
          }}
        ></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-display text-radio-blue mb-4">
              Контакты и сотрудничество
            </h2>
            <div className="w-24 h-1 bg-radio-blue/30 mx-auto rounded-full"></div>
            <p className="text-gray-300 leading-relaxed pt-5 max-w-4xl mx-auto">
              Привет! Я - Саня Nuvertal. <br />
              Веду шоу «Радио Саня», создаю музыку в стиле Drum&Bass и выпускаю подкаст Liquor с
              отборными треками для всех ценителей ломаных ритмов. <br />
              Также занимаюсь продакшн-стримами, обучением и помогаю артистам выводить звук на новый
              уровень.
            </p>
          </div>

          <div className="bg-black/50 backdrop-blur-md rounded-xl overflow-hidden border border-radio-blue/20 shadow-lg shadow-radio-blue/5 bg-gradient-to-br from-radio-dark-blue to-black/70">
            <div className="md:flex">
              {/* Right side - Services */}
              <div className="md:w-1/2 p-8 md:p-10 bg-black/30">
                <h3 className="text-xl font-display text-radio-blue mb-6">
                  Услуги и сотрудничество
                </h3>

                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="mt-1 p-2 rounded-full bg-radio-blue/10">
                      <BookOpen className="w-5 h-5 text-radio-blue" />
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-gray-200 mb-1">Обучение</h4>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        Помогу разобраться в тонкостях продакшена Drum&Bass и электронной музыки.
                        Личные уроки под ваши цели: от старта до продвинутой работы с миксом.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="mt-1 p-2 rounded-full bg-radio-blue/10">
                      <Headphones className="w-5 h-5 text-radio-blue" />
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-gray-200 mb-1">Продюсирование</h4>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        Создание и доработка треков, микширование, мастеринг, консультации по
                        аранжировке и финальному звуку.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="mt-1 p-2 rounded-full bg-radio-blue/10">
                      <Calendar className="w-5 h-5 text-radio-blue" />
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-gray-200 mb-1">Мероприятия</h4>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        Выступления на рейвах, вечеринках и онлайн-эвентах. Возможна организация
                        мероприятий и творческие коллаборации.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Left side - Contact info */}
              <div className="md:w-1/2 p-8 md:p-10">
                <div className="space-y-8">
                  <div className="space-y-4">
                    <h3 className="text-xl font-display text-radio-blue mb-4">
                      Музыкальные платформы
                    </h3>

                    {musicPlatformLinks.map((link, index) => (
                      <Link
                        key={index}
                        href={link.url}
                        onClick={() => reachGoal(`music-platform-${link.id}-click`)}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${link.name}: ${link.description}`}
                        className="flex items-center space-x-3 group hover:bg-black/30 p-2 rounded-lg transition-colors"
                      >
                        <div className="w-10 h-10 rounded-full bg-radio-blue/10 flex items-center justify-center group-hover:bg-radio-blue/20 transition-colors">
                          {link.icon}
                        </div>
                        <div>
                          <div className="flex items-center">
                            <p className="text-gray-200 font-medium">{link.name}</p>
                            <ExternalLink className="w-3.5 h-3.5 ml-1.5 text-gray-400 group-hover:text-radio-blue transition-colors" />
                          </div>
                          <p className="text-gray-400 text-sm">{link.description}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="mt-8">
                  <h3 className="text-xl font-display text-radio-blue mb-4">Связаться со мной</h3>

                  <div className="space-y-4 mb-6">
                    <Link
                      href="https://t.me/nuvertal"
                      target="_blank"
                      onClick={() => reachGoal('telegram-contact-link-click')}
                      rel="noopener noreferrer"
                      className="flex items-center space-x-3 group hover:bg-black/30 p-2 rounded-lg transition-colors"
                      aria-label="Связаться с Nuvertal в Telegram - @nuvertal"
                    >
                      <div className="w-10 h-10 rounded-full bg-radio-blue/10 flex items-center justify-center group-hover:bg-radio-blue/20 transition-colors">
                        <TelegramIcon
                          width={20}
                          height={20}
                          className={clsx(SOCIAL_ICON_DIMENSIONS, 'text-[#38b0e3] p-2')}
                        />
                      </div>
                      <div>
                        <p className="text-gray-200 font-medium">Telegram</p>
                        <p className="text-gray-400 text-sm">@nuvertal</p>
                      </div>
                    </Link>

                    <Link
                      href="mailto:contact@radiosanya.ru"
                      onClick={() => reachGoal('email-contact-link-click')}
                      className="flex items-center space-x-3 group hover:bg-black/30 p-2 rounded-lg transition-colors"
                      aria-label="Связаться с Nuvertal по электронной почте - contact@radiosanya.ru"
                    >
                      <div className="w-10 h-10 rounded-full bg-radio-blue/10 flex items-center justify-center group-hover:bg-radio-blue/20 transition-colors">
                        <Mail className="w-5 h-5 text-radio-blue" />
                      </div>
                      <div>
                        <p className="text-gray-200 font-medium">Email</p>
                        <p className="text-gray-400 text-sm">contact@radiosanya.ru</p>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Social Media Links */}
          <div className="mt-20">
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 mb-8">
              <div className="relative w-32 h-32 md:w-40 md:h-40">
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-radio-blue/30 to-radio-blue/10 animate-pulse"></div>
                <div className="absolute inset-1 rounded-full overflow-hidden bg-black/50 backdrop-blur-sm border-2 border-radio-blue/30">
                  <video className="w-full h-full object-cover" autoPlay loop muted playsInline>
                    <source src="/assets/videos/good-idea.mp4" type="video/mp4" />
                  </video>
                </div>
              </div>

              <div className="text-center md:text-left max-w-lg">
                <h3 className="text-2xl font-display text-radio-blue mb-3">
                  Следите за «Радио Саня»
                </h3>
                <p className="text-gray-300">
                  Будьте с нами на связи: анонсы стримов, новые треки, лучшие моменты эфиров и жизнь
                  проекта — всё в наших соцсетях. Присоединяйтесь к комьюнити и оставайтесь на
                  волне!
                </p>
              </div>
            </div>

            <div className="bg-black/30 backdrop-blur-sm rounded-xl p-6 border border-radio-blue/10 max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {socialLinks.map((social, index) => (
                  <Link
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${social.name}: ${social.description}`}
                    className="flex items-center group hover:bg-black/30 p-3 rounded-lg transition-colors border border-transparent hover:border-radio-blue/20"
                    onClick={() => reachGoal(`footer-${social.id}-click`)}
                  >
                    <div
                      className={`w-10 h-10 rounded-full ${social.bgColor} flex items-center justify-center mr-3`}
                    >
                      {social.icon}
                    </div>
                    <div>
                      <div className="flex items-center">
                        <p className="text-gray-200 font-medium">{social.name}</p>
                        <ExternalLink className="w-3.5 h-3.5 ml-1.5 text-gray-400 group-hover:text-radio-blue transition-colors" />
                      </div>
                      <p className="text-gray-400 text-sm">{social.description}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
